import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AdminLayout } from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Download, Package, Route as RouteIcon, Truck, TrendingUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

const todayISO = () => new Date().toISOString().split('T')[0];
const daysAgoISO = (n) => {
  const d = new Date(); d.setDate(d.getDate() - n);
  return d.toISOString().split('T')[0];
};

export default function AdminResumenDiario() {
  // Single-day KPIs (kept for the existing /admin/resumen-diario endpoint)
  const [fecha, setFecha] = useState(todayISO());
  const [resumen, setResumen] = useState(null);
  const [loading, setLoading] = useState(true);

  // Range used only for PDF/XLSX exports
  const [rangeFrom, setRangeFrom] = useState(daysAgoISO(6));
  const [rangeTo, setRangeTo] = useState(todayISO());
  const [exporting, setExporting] = useState(null);

  const fetchResumen = async (targetDate) => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/admin/resumen-diario`, {
        headers: authHeaders(),
        params: { fecha: targetDate }
      });
      setResumen(resp.data);
    } catch (err) {
      toast.error('Error al cargar resumen diario');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchResumen(fecha); /* eslint-disable-next-line */ }, []);

  const handleExport = async (format) => {
    if (!rangeFrom || !rangeTo) { toast.error('Selecciona el rango de fechas'); return; }
    setExporting(format);
    try {
      const resp = await axios.get(`${API}/admin/resumen/export.${format}`, {
        headers: authHeaders(),
        params: { fecha_desde: rangeFrom, fecha_hasta: rangeTo },
        responseType: 'blob',
      });
      const blobUrl = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `resumen_${rangeFrom}_${rangeTo}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success(`Resumen ${format.toUpperCase()} descargado`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : `Error al exportar ${format.toUpperCase()}`);
    } finally {
      setExporting(null);
    }
  };

  const KPICard = ({ icon: Icon, title, value, subtitle, color }) => (
    <div className="bg-white rounded-xl border border-slate-200 p-6">
      <div className="flex items-start justify-between mb-3">
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon size={24} className="text-white" />
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold text-slate-900">{value}</p>
          <p className="text-xs text-slate-500 mt-1">{subtitle}</p>
        </div>
      </div>
      <p className="text-sm font-medium text-slate-600">{title}</p>
    </div>
  );

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <div>
            <h1 className="text-xl font-semibold text-[#374B67]">Resumen Operativo</h1>
            <p className="text-sm text-slate-500">KPIs del día seleccionado · Exporta el rango deseado a PDF o Excel.</p>
          </div>
          <div className="flex items-end gap-2">
            <div>
              <Label className="text-xs text-slate-600">Día (KPIs)</Label>
              <Input type="date" value={fecha}
                onChange={(e) => { setFecha(e.target.value); fetchResumen(e.target.value); }}
                className="w-40" data-testid="resumen-date-input" />
            </div>
          </div>
        </div>

        {/* Export bar */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 mb-5">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div>
              <Label className="text-xs text-slate-600">Desde</Label>
              <Input type="date" value={rangeFrom} onChange={e => setRangeFrom(e.target.value)} data-testid="resumen-range-desde" />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Hasta</Label>
              <Input type="date" value={rangeTo} onChange={e => setRangeTo(e.target.value)} data-testid="resumen-range-hasta" />
            </div>
            <Button onClick={() => handleExport('xlsx')} variant="outline" disabled={exporting === 'xlsx'}
              data-testid="resumen-export-xlsx">
              <Download size={14} className="mr-2" />
              {exporting === 'xlsx' ? 'Generando…' : 'Exportar Excel'}
            </Button>
            <Button onClick={() => handleExport('pdf')} className="bg-[#009940] hover:bg-[#007F35] text-white"
              disabled={exporting === 'pdf'} data-testid="resumen-export-pdf">
              <Download size={14} className="mr-2" />
              {exporting === 'pdf' ? 'Generando…' : 'Exportar PDF'}
            </Button>
          </div>
          <p className="text-[11px] text-slate-400 mt-2">Rango máximo: 93 días.</p>
        </div>

        {loading ? (
          <div className="text-center py-12 text-slate-500">Cargando…</div>
        ) : !resumen ? (
          <div className="text-center py-12 text-slate-500">No hay datos para este día.</div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <KPICard icon={Package} title="Total Solicitudes" value={resumen.solicitudes.total} subtitle="del día" color="bg-blue-500" />
              <KPICard icon={RouteIcon} title="Rutas Completadas"
                value={`${resumen.rutas.completadas}/${resumen.rutas.total}`}
                subtitle={`${Math.round((resumen.rutas.completadas / resumen.rutas.total) * 100) || 0}% completado`}
                color="bg-green-500" />
              <KPICard icon={Truck} title="Vehículos Utilizados"
                value={`${resumen.vehiculos.utilizados}/${resumen.vehiculos.total_disponibles}`}
                subtitle="en operación" color="bg-cyan-500" />
              <KPICard icon={TrendingUp} title="Km Totales" value={resumen.rutas.total_km}
                subtitle={`${resumen.rutas.avg_km_per_route} km/ruta`} color="bg-purple-500" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="font-semibold text-slate-900 mb-4">Solicitudes por Estado</h3>
                <div className="space-y-3">
                  {Object.entries(resumen.solicitudes.por_estado).map(([estado, count]) => (
                    <div key={estado} className="flex items-center justify-between">
                      <span className="text-sm text-slate-600">{estado}</span>
                      <span className="text-sm font-semibold text-slate-900">{count}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="font-semibold text-slate-900 mb-4">Top Zonas</h3>
                <div className="space-y-3">
                  {resumen.solicitudes.por_zona.slice(0, 5).map((z, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <span className="text-sm text-slate-600">{z.zona || 'Sin zona'}</span>
                      <span className="text-sm font-semibold text-slate-900">{z.count}</span>
                    </div>
                  ))}
                  {resumen.solicitudes.por_zona.length === 0 && (
                    <p className="text-xs text-slate-400">Sin datos</p>
                  )}
                </div>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="font-semibold text-slate-900 mb-4">Métricas de Rutas</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Total paradas</span>
                    <span className="text-sm font-semibold text-slate-900">{resumen.rutas.total_stops}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Paradas promedio/ruta</span>
                    <span className="text-sm font-semibold text-slate-900">{resumen.rutas.avg_stops_per_route}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Tiempo total</span>
                    <span className="text-sm font-semibold text-slate-900">{resumen.rutas.total_time_minutes} min</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Distancia total</span>
                    <span className="text-sm font-semibold text-slate-900">{resumen.rutas.total_km} km</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="font-semibold text-slate-900 mb-4">Eficiencia Operativa</h3>
                <div className="h-40 flex items-center justify-center text-slate-400 text-sm">
                  Gráfico de tendencias (próximamente)
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
